import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Copy, Check, ChevronDown, Trash2, Sparkles, Download, FileDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { trackEvent } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Axiom branding colors
const AXIOM_COLORS = {
  primary: '#102A43',      // Dark navy
  secondary: '#334E68',    // Slate
  accent: '#2F80ED',       // Electric blue
  accentHover: '#1E6FD9',
  background: '#F0F4F8',
  surface: '#FFFFFF',
  text: '#102A43',
  textMuted: '#627D98',
  border: '#D9E2EC',
};

function AxiomChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showPrompts, setShowPrompts] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(null); // 'response' | 'conversation' | null
  const [assessmentContext, setAssessmentContext] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Handle opening Axiom with context from assessment results
  const handleOpenWithContext = useCallback((event) => {
    const { initialMessage } = event.detail || {};
    
    // Check for assessment context in session storage
    const contextStr = sessionStorage.getItem('axiom_context');
    if (contextStr) {
      try {
        const context = JSON.parse(contextStr);
        setAssessmentContext(context);
      } catch (e) {
        console.error('Failed to parse Axiom context:', e);
      }
    }
    
    setIsOpen(true);
    
    // Track Axiom opened
    trackEvent('axiom_opened', {
      has_context: !!contextStr,
      source: 'assessment_result'
    });
    
    // If there's an initial message, send it
    if (initialMessage) {
      setTimeout(() => {
        handleSend(initialMessage);
      }, 500);
    }
  }, []);

  // Listen for openAxiom events
  useEffect(() => {
    window.addEventListener('openAxiom', handleOpenWithContext);
    return () => {
      window.removeEventListener('openAxiom', handleOpenWithContext);
    };
  }, [handleOpenWithContext]);

  // Fetch Axiom status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_URL}/api/axiom/status`, { headers });
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Failed to fetch Axiom status:', error);
    }
  };

  const handleSend = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading) return;
    if (status && !status.can_send) {
      alert('Daily message limit reached. Upgrade to a paid plan for unlimited access.');
      return;
    }

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowPrompts(false);

    // Create assistant message placeholder
    const assistantMessageId = (Date.now() + 1).toString();
    setMessages(prev => [...prev, {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    }]);

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      abortControllerRef.current = new AbortController();

      const response = await fetch(`${API_URL}/api/axiom/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: messageText.trim(),
          session_id: sessionId,
          assessment_context: assessmentContext
        }),
        signal: abortControllerRef.current.signal
      });

      // Track message sent
      trackEvent('axiom_message_sent', {
        session_id: sessionId,
        has_assessment_context: !!assessmentContext
      });

      if (!response.ok) {
        const error = await response.json();
        
        // Handle rate limiting specifically
        if (response.status === 429) {
          const retryAfter = error.retry_after_seconds || 60;
          const message = error.error_code === 'RATE_LIMIT_EXCEEDED'
            ? `You're sending messages too quickly. Please wait ${retryAfter} seconds and try again.`
            : error.detail || 'Daily message limit reached. Please try again tomorrow.';
          throw new Error(message);
        }
        
        throw new Error(error.detail || 'Failed to get response');
      }

      // Get session ID from header
      const newSessionId = response.headers.get('X-Session-Id');
      if (newSessionId) {
        setSessionId(newSessionId);
      }

      // Read streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            
            // Parse JSON-encoded chunk to properly handle newlines
            try {
              const decodedChunk = JSON.parse(data);
              fullContent += decodedChunk;
            } catch {
              // Fallback to raw data if not JSON
              fullContent += data;
            }
            
            setMessages(prev => prev.map(msg =>
              msg.id === assistantMessageId
                ? { ...msg, content: fullContent }
                : msg
            ));
          }
        }
      }

      // Mark as done streaming
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? { ...msg, isStreaming: false }
          : msg
      ));

      // Refresh status to update message count
      fetchStatus();

    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled
        setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
      } else {
        console.error('Axiom error:', error);
        setMessages(prev => prev.map(msg =>
          msg.id === assistantMessageId
            ? { ...msg, content: `*Error: ${error.message}*`, isStreaming: false }
            : msg
        ));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePromptClick = (prompt) => {
    handleSend(prompt.text);
  };

  const copyToClipboard = async (content, messageId) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(messageId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const exportResponsePdf = async (content, messageId) => {
    setExportingPdf(messageId);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const response = await fetch(`${API_URL}/api/axiom/export-pdf`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content,
          title: 'Axiom Response'
        })
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `axiom-response-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExportingPdf(null);
    }
  };

  const exportConversationPdf = async () => {
    if (!sessionId) return;
    setExportingPdf('conversation');
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API_URL}/api/axiom/conversation/${sessionId}/export-pdf`, {
        method: 'GET',
        headers
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `axiom-conversation-${sessionId.slice(0,8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export conversation PDF:', error);
      alert('Failed to export conversation. Please try again.');
    } finally {
      setExportingPdf(null);
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setSessionId(null);
    setShowPrompts(true);
    fetchStatus();
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchStatus();
    }
  };

  if (!status?.enabled) {
    return null; // Don't render if Axiom is disabled
  }

  return (
    <>
      {/* Floating Launcher Button */}
      <button
        onClick={toggleOpen}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen ? 'rotate-90' : ''
        }`}
        style={{ 
          backgroundColor: AXIOM_COLORS.accent,
          boxShadow: '0 4px 20px rgba(47, 128, 237, 0.4)'
        }}
        data-testid="axiom-launcher"
        aria-label={isOpen ? 'Close Axiom' : 'Open Axiom'}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Sparkles className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 w-[400px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-150px)] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ 
            backgroundColor: AXIOM_COLORS.surface,
            border: `1px solid ${AXIOM_COLORS.border}`
          }}
          data-testid="axiom-panel"
        >
          {/* Header */}
          <div 
            className="px-5 py-4 flex items-center justify-between"
            style={{ 
              background: `linear-gradient(135deg, ${AXIOM_COLORS.primary} 0%, ${AXIOM_COLORS.secondary} 100%)` 
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Axiom</h3>
                <p className="text-white/70 text-xs">AI Governance Intelligence Layer</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && sessionId && (
                <button
                  onClick={exportConversationPdf}
                  disabled={exportingPdf === 'conversation'}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
                  title="Export conversation as PDF"
                >
                  <FileDown className="w-4 h-4 text-white/70" />
                </button>
              )}
              {messages.length > 0 && (
                <button
                  onClick={clearConversation}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Clear conversation"
                >
                  <Trash2 className="w-4 h-4 text-white/70" />
                </button>
              )}
              <button
                onClick={toggleOpen}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <ChevronDown className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Usage indicator */}
          {status && !status.is_paid && (
            <div 
              className="px-4 py-2 text-xs flex items-center justify-between"
              style={{ backgroundColor: AXIOM_COLORS.background }}
            >
              <span style={{ color: AXIOM_COLORS.textMuted }}>
                {status.messages_today}/{status.messages_limit} messages today
              </span>
              <a 
                href="/apply" 
                className="font-medium hover:underline"
                style={{ color: AXIOM_COLORS.accent }}
              >
                Upgrade for unlimited
              </a>
            </div>
          )}

          {/* Messages Area */}
          <div 
            className="flex-1 overflow-y-auto p-4 space-y-4"
            style={{ backgroundColor: AXIOM_COLORS.background }}
          >
            {/* Welcome message when empty */}
            {messages.length === 0 && showPrompts && (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: `${AXIOM_COLORS.accent}15` }}>
                  <Sparkles className="w-8 h-8" style={{ color: AXIOM_COLORS.accent }} />
                </div>
                <h4 className="font-semibold text-lg mb-2" style={{ color: AXIOM_COLORS.text }}>
                  Ask. Document. Govern.
                </h4>
                <p className="text-sm mb-6" style={{ color: AXIOM_COLORS.textMuted }}>
                  Your AI governance assistant. Ask about frameworks, create documentation, or get guidance.
                </p>
                
                {/* Suggested Prompts */}
                <div className="space-y-2">
                  {status?.suggested_prompts?.slice(0, 5).map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => handlePromptClick(prompt)}
                      className="w-full text-left px-4 py-3 rounded-xl text-sm transition-all hover:scale-[1.02]"
                      style={{ 
                        backgroundColor: AXIOM_COLORS.surface,
                        color: AXIOM_COLORS.text,
                        border: `1px solid ${AXIOM_COLORS.border}`
                      }}
                      data-testid={`axiom-prompt-${idx}`}
                    >
                      {prompt.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message list */}
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    message.role === 'user' 
                      ? 'rounded-br-md' 
                      : 'rounded-bl-md'
                  }`}
                  style={{
                    backgroundColor: message.role === 'user' 
                      ? AXIOM_COLORS.accent 
                      : AXIOM_COLORS.surface,
                    color: message.role === 'user' 
                      ? 'white' 
                      : AXIOM_COLORS.text,
                    border: message.role === 'assistant' 
                      ? `1px solid ${AXIOM_COLORS.border}` 
                      : 'none'
                  }}
                >
                  {message.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({children}) => <h1 className="text-lg font-bold mt-4 mb-2">{children}</h1>,
                          h2: ({children}) => <h2 className="text-base font-semibold mt-3 mb-2">{children}</h2>,
                          h3: ({children}) => <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>,
                          p: ({children}) => <p className="text-sm mb-2 leading-relaxed">{children}</p>,
                          ul: ({children}) => <ul className="text-sm list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                          ol: ({children}) => <ol className="text-sm list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                          li: ({children}) => <li className="text-sm">{children}</li>,
                          strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                          em: ({children}) => <em className="italic text-gray-600">{children}</em>,
                          blockquote: ({children}) => (
                            <blockquote className="border-l-4 pl-3 my-2 italic text-gray-600" style={{ borderColor: AXIOM_COLORS.accent }}>
                              {children}
                            </blockquote>
                          ),
                          code: ({inline, children}) => inline 
                            ? <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
                            : <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto"><code>{children}</code></pre>,
                          table: ({children}) => (
                            <div className="overflow-x-auto my-2">
                              <table className="min-w-full text-xs border-collapse">{children}</table>
                            </div>
                          ),
                          th: ({children}) => <th className="border px-2 py-1 bg-gray-100 font-semibold text-left">{children}</th>,
                          td: ({children}) => <td className="border px-2 py-1">{children}</td>,
                          hr: () => <hr className="my-3 border-gray-200" />,
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                      
                      {/* Copy button for assistant messages */}
                      {!message.isStreaming && message.content && (
                        <div className="flex items-center gap-3 mt-2">
                          <button
                            onClick={() => copyToClipboard(message.content, message.id)}
                            className="flex items-center gap-1 text-xs hover:underline"
                            style={{ color: AXIOM_COLORS.textMuted }}
                          >
                            {copiedId === message.id ? (
                              <>
                                <Check className="w-3 h-3" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                Copy response
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => exportResponsePdf(message.content, message.id)}
                            disabled={exportingPdf === message.id}
                            className="flex items-center gap-1 text-xs hover:underline disabled:opacity-50"
                            style={{ color: AXIOM_COLORS.textMuted }}
                          >
                            <Download className="w-3 h-3" />
                            {exportingPdf === message.id ? 'Exporting...' : 'Export PDF'}
                          </button>
                        </div>
                      )}
                      
                      {/* Streaming indicator */}
                      {message.isStreaming && (
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div 
            className="p-4 border-t"
            style={{ 
              backgroundColor: AXIOM_COLORS.surface,
              borderColor: AXIOM_COLORS.border 
            }}
          >
            <div 
              className="flex items-end gap-2 rounded-xl p-2"
              style={{ backgroundColor: AXIOM_COLORS.background }}
            >
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about AI governance..."
                className="flex-1 resize-none bg-transparent border-none outline-none text-sm min-h-[40px] max-h-[120px] py-2 px-2"
                style={{ color: AXIOM_COLORS.text }}
                rows={1}
                disabled={isLoading || (status && !status.can_send)}
                data-testid="axiom-input"
              />
              <button
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || isLoading}
                className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backgroundColor: inputValue.trim() && !isLoading ? AXIOM_COLORS.accent : 'transparent',
                  color: inputValue.trim() && !isLoading ? 'white' : AXIOM_COLORS.textMuted
                }}
                data-testid="axiom-send"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            
            {/* Disclaimer */}
            <p className="text-[10px] text-center mt-2" style={{ color: AXIOM_COLORS.textMuted }}>
              Axiom provides educational information only. Not legal or compliance advice.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default AxiomChat;
